/**
 * 気象観測地点の画面内一時切替 (weather-point-in-screen-temp-switch design)。
 *
 * 一時切替の state は page-local な ephemeral React state で持ち、永続化しない
 * (design D4)。断面切替・リロード・別画面移動は DemandForecastDailyPage ごと
 * remount させる既存の遷移経路 (List 経由) に乗るため、その mount 境界で本 state
 * も自然に消滅し、AC-DFC-008b の「都度復帰」を満たす。念のため section
 * (対象日で判定した計画断面) が変わった時にも明示 reset する (design D4 の実装詳細注記)。
 *
 * 候補・組織共通設定の地点名は既存の `useListWeatherForecastPoints` (Admin
 * 画面と共有) を選択中エリアで絞って再利用する — 新規 RPC は追加しない
 * (design Contract/Data)。
 */
import { useEffect, useMemo, useState } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'

import { useListWeatherForecastPoints } from '~/pages/AdminWeatherForecastPoints/hooks/useListWeatherForecastPoints'

type Params = {
  areaCode: AreaCode | undefined
  /** 断面 (対象日で判定した計画断面)。変化したら一時切替を明示的に解除する。 */
  sectionSegment: string | undefined
  /**
   * 画面が read-only (WF 全体完了 / 削除済み / 需要想定確定) に切り替わったか。
   * true になった瞬間 override を明示 reset し、以降 undefined を返す
   * (design demand/forecast-today.html L3529-3531 / L3573-3574: 確定完了は
   * 組織共通設定への自動復帰 trigger。同一 mount 内で確定した場合、UI 側の
   * hide (D6) だけでは request seam に override が残り続けるため、状態を
   * 落として request key も戻す。PR #6000 review B1-3)。
   */
  isReadonly: boolean
}

export type WeatherPointCandidate = {
  locationName: string
  isAvailable: boolean
}

export type WeatherPointOverrideState = {
  /** 一時切替中の地点名。 undefined = 組織共通設定のまま (未切替)。 */
  overrideLocationName: string | undefined
  /** 選択中エリアの候補地点 (is_available 込み)。 */
  candidates: WeatherPointCandidate[]
  /** 選択中エリアの組織共通設定の地点名 (未設定領域は候補先頭の既定値へフォールバック)。 */
  orgDefaultLocationName: string | undefined
  /**
   * select の選択が変わった時に呼ぶ。 組織共通設定と同じ値を選び直した場合は
   * 一時切替を解除する (= 「戻す」と同じ扱い、無用な override 状態行を出さない)。
   */
  onSelectLocationName: (locationName: string | undefined) => void
  /** 「戻す」操作: 一時切替を解除し組織共通設定の地点へ復帰する。 */
  onReset: () => void
}

/** 気象観測地点の画面内一時切替の state と候補を管理する。 */
export function useWeatherPointOverride({
  areaCode,
  sectionSegment,
  isReadonly,
}: Params): WeatherPointOverrideState {
  // AC-DFC-008b: 断面切替は一時切替を解除する。DemandForecastDailyPage は
  // section 切替のたびに List 経由で unmount/remount される想定 (design D4 fact)
  // だが、その前提が崩れた場合の保険として render 時点で同期的に無効化する。
  // section とセットで持つのは、useEffect の遅延で 1 render 分だけ「新 section +
  // 前 override」の request key が useGetDemandForecastDaily に流れて余分な
  // fetch が走るのを防ぐため (PR #6000 review)。setter は現 section と一緒に
  // 格納し、getter は sectionSegment 不一致 or isReadonly=true なら undefined を返す。
  const [override, setOverride] = useState<
    { locationName: string; section: string | undefined } | undefined
  >(undefined)
  const overrideLocationName =
    override !== undefined && override.section === sectionSegment && !isReadonly
      ? override.locationName
      : undefined

  // stale な override (section 不一致 or isReadonly=true) は state からも消して
  // おく (isReadonly→false に戻ることは実運用では無いが、次回同一 section に
  // 戻った時の復活防止と対称性のため)。
  useEffect(() => {
    setOverride((prev) =>
      prev !== undefined && (prev.section !== sectionSegment || isReadonly)
        ? undefined
        : prev,
    )
  }, [sectionSegment, isReadonly])

  // 一時切替は auxiliary 表示機能なので、地点一覧 fetch 失敗を app-wide error
  // boundary へ throw させず data=undefined で degrade する (PR #6000 review)。
  // 失敗時は candidates 空 → switch UI 自体が render されない = 需要想定画面の
  // 編集経路は無事、という gracefully-degraded 挙動。
  const { data } = useListWeatherForecastPoints({ throwOnError: false })

  const candidates = useMemo<WeatherPointCandidate[]>(() => {
    const forArea = data?.candidates.find((c) => c.areaCode === areaCode)
    return (forArea?.candidates ?? []).map((c) => ({
      locationName: c.locationName,
      isAvailable: c.isAvailable,
    }))
  }, [data, areaCode])

  const orgDefaultLocationName = useMemo<string | undefined>(() => {
    const saved = data?.points.find(
      (p) => p.areaCode === areaCode,
    )?.locationName
    if (saved !== undefined && saved !== '') return saved
    // 組織未設定のエリアは候補先頭 (system-fixed default) を表示用に採用する
    // (backend weatherpoint.Resolve の org-default fallback と同じ意図の表示、
    // ただし本 state row は表示専用でありこの値を request には使わない)。
    return candidates[0]?.locationName
  }, [data, areaCode, candidates])

  return {
    overrideLocationName,
    candidates,
    orgDefaultLocationName,
    onSelectLocationName: (locationName) => {
      if (
        locationName === undefined ||
        locationName === orgDefaultLocationName
      ) {
        setOverride(undefined)
        return
      }
      setOverride({ locationName, section: sectionSegment })
    },
    onReset: () => {
      setOverride(undefined)
    },
  }
}
